const REFERENCE_ERROR_TYPE = 'ReferenceError';
const GOOGLETAG_UNDEFINED_VALUE = 'googletag is not defined';

// Sentry renders the browser-errors mechanism as
// "auto.browser.browserapierrors.setInterval". Depending on SDK version it is
// encoded either as a single literal in `mechanism.type`, or decomposed as
// `mechanism.type = 'browserapierrors'` with `mechanism.value = 'setInterval'`.
// Matching either encoding keeps the predicate narrow while surviving SDK
// packaging differences. Any other timer (e.g. setTimeout) or mechanism shape
// fails open and the event is kept.
const SET_INTERVAL_MECHANISM_LITERAL =
  'auto.browser.browserapierrors.setInterval';
const BROWSER_ERRORS_MECHANISM_TYPE = 'browserapierrors';
const SET_INTERVAL_MECHANISM_VALUE = 'setInterval';

type FrameLike = {
  filename?: string;
  abs_path?: string;
};

type MechanismLike = {
  type?: string;
  value?: string;
};

type ExceptionLike = {
  type?: string;
  value?: string;
  mechanism?: MechanismLike;
  stacktrace?: {
    frames?: FrameLike[];
  };
};

type InjectedGoogleTagIntervalEventLike = {
  exception?: {
    values?: ExceptionLike[];
  };
};

const LIPS_SOURCE_PATTERNS = ['/_next/', '/src/', 'lips.org.uk'];

// Sentry SDK / Application Insights telemetry frames must not count as LIPS
// business code. An injected-timer failure whose only app-looking frames are
// instrumentational plumbing is still external noise.
const INSTRUMENTATION_PATTERNS = [
  '@sentry',
  'sentry',
  'applicationinsights',
  'appinsights',
];

const framePath = (frame: FrameLike): string =>
  `${frame.abs_path ?? ''} ${frame.filename ?? ''}`;

const isLipsSourcePath = (frame: FrameLike): boolean =>
  LIPS_SOURCE_PATTERNS.some((pattern) => framePath(frame).includes(pattern));

const isInstrumentationFrame = (frame: FrameLike): boolean =>
  INSTRUMENTATION_PATTERNS.some((pattern) =>
    framePath(frame).includes(pattern),
  );

const isLipsBusinessFrame = (frame: FrameLike): boolean =>
  isLipsSourcePath(frame) && !isInstrumentationFrame(frame);

const isAnonymousFrame = (frame: FrameLike): boolean =>
  framePath(frame).includes('<anonymous>');

const hasSetIntervalMechanism = (
  mechanism: MechanismLike | undefined,
): boolean =>
  mechanism !== undefined &&
  (mechanism.type === SET_INTERVAL_MECHANISM_LITERAL ||
    (mechanism.type === BROWSER_ERRORS_MECHANISM_TYPE &&
      mechanism.value === SET_INTERVAL_MECHANISM_VALUE));

const isEligibleException = (exception: ExceptionLike): boolean =>
  exception.type === REFERENCE_ERROR_TYPE &&
  exception.value?.trim() === GOOGLETAG_UNDEFINED_VALUE;

export function isKnownInjectedGoogleTagIntervalError(event: unknown): boolean {
  if (event === null || typeof event !== 'object') {
    return false;
  }

  const candidate = event as InjectedGoogleTagIntervalEventLike;
  const exceptions = candidate.exception?.values;

  if (!exceptions?.some(isEligibleException)) {
    return false;
  }

  if (
    !exceptions.some((exception) =>
      hasSetIntervalMechanism(exception.mechanism),
    )
  ) {
    return false;
  }

  const frames = exceptions.flatMap(
    (exception) => exception.stacktrace?.frames ?? [],
  );

  // A real LIPS frame makes this a product bug candidate — never hide it.
  if (frames.some(isLipsBusinessFrame)) {
    return false;
  }

  // Require at least one anonymous/external frame, as seen in production
  // ("<anonymous>:1:519 ... + Sentry wrapper"). Absent that, fail open.
  return frames.some(isAnonymousFrame);
}
